import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { 
  FileText, 
  Search, 
  Building2, 
  Loader2, 
  Download, 
  AlertTriangle,
  Calendar,
  DollarSign,
  User,
  RefreshCw,
  FileWarning,
  Scale,
  CheckCircle,
  Calculator,
  Plus,
  Eye,
  Clock,
  ArrowLeft
} from "lucide-react";
import api from "@/lib/api";

export default function LegalNotices() {
  const [view, setView] = useState("list"); // "list" or "create"
  const [loading, setLoading] = useState(false);
  const [lookingUpLoan, setLookingUpLoan] = useState(false);
  const [lookingUpCompany, setLookingUpCompany] = useState(false);
  const [generatedDocs, setGeneratedDocs] = useState([]);
  const [generating, setGenerating] = useState(false);
  
  // Document selection
  const [selectedDocs, setSelectedDocs] = useState({
    default_notice: true,
    letter_before_action: false
  });
  
  // Form state
  const [formData, setFormData] = useState({
    sfa_number: "SFA-",
    company_name: "",
    acn: "",
    abn: "",
    registered_address: "",
    loan_amount: "",
    factor_amount: "",
    outstanding_amount: "",
    payments_received: "",
    default_fee: "",
    late_payment_fees: "",
    director_name: "",
    director_address: "",
    agreement_date: "",
    default_date: "",
    notice_date: new Date().toISOString().split('T')[0],
    remedy_days: "7",
    breach_details: "",
    payment_amount: "",
    payment_frequency: "week",
    last_payment_date: "",
    first_missed_payment_date: "",
    payment_amounts_fallen_due: "",
    overall_repayment_amount: "",
    upfront_fees: ""
  });

  // Data loaded indicator
  const [dataLoaded, setDataLoaded] = useState(false);

  // Calculate default fee when loan amount changes
  useEffect(() => {
    if (formData.loan_amount && !formData.default_fee) {
      const defaultFee = parseFloat(formData.loan_amount) * 0.10;
      setFormData(prev => ({ ...prev, default_fee: defaultFee.toFixed(2) }));
    }
  }, [formData.loan_amount]);

  // Calculate total due — prefer the live LoanDisk balance (already net of
  // all payments and inclusive of loan fees/penalties) + default fee
  const calculateTotalDue = () => {
    const defaultFee = parseFloat(formData.default_fee) || 0;
    const outstanding = parseFloat(formData.outstanding_amount) || 0;
    if (outstanding > 0) return outstanding + defaultFee;
    const principal = parseFloat(formData.loan_amount) || 0;
    const factor = parseFloat(formData.factor_amount) || 0;
    const lateFees = parseFloat(formData.late_payment_fees) || 0;
    const payments = parseFloat(formData.payments_received) || 0;
    return principal + factor + defaultFee + lateFees - payments;
  };

  // Lookup loan from LoanDisk
  const lookupLoanDisk = async () => {
    const sfaNum = formData.sfa_number.trim();
    if (!sfaNum || sfaNum === "SFA-") {
      toast.error("Please enter an SFA number");
      return;
    }
    
    setLookingUpLoan(true);
    try {
      const response = await api.get(`/aus/legal-notices/loandisk/${sfaNum}`);
      
      if (response.data.found) {
        const data = response.data;
        const principal = data.loan_principal || 0;
        const factorAmount = data.loan_interest_amount || 0;
        const defaultFee = (principal * 0.10).toFixed(2);
        const paymentsReceived = data.total_paid || 0;
        const upfrontFees = data.upfront_fees || 0;
        // Penalties charged to date (gross) — pairs with the gross fallen-due
        // figure so Arrears = fallen due + fees − payments == LoanDisk pending_due
        const lateFees = data.penalty_charged != null ? data.penalty_charged : (data.penalty_balance || 0);

        // Format date from DD/MM/YYYY to YYYY-MM-DD
        const toISO = (ddmmyyyy) => {
          if (!ddmmyyyy) return "";
          const parts = String(ddmmyyyy).split('/');
          return parts.length === 3 ? `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}` : "";
        };
        const agreementDate = toISO(data.disbursement_date);

        setFormData(prev => ({
          ...prev,
          company_name: data.borrower_name || prev.company_name,
          registered_address: data.borrower_address || prev.registered_address,
          loan_amount: principal?.toString() || prev.loan_amount,
          factor_amount: factorAmount?.toFixed(2) || "",
          outstanding_amount: data.loan_balance?.toString() || prev.outstanding_amount,
          default_fee: defaultFee,
          late_payment_fees: lateFees.toFixed(2),
          payments_received: paymentsReceived?.toFixed(2) || "",
          payment_amounts_fallen_due: data.gross_fallen_due != null ? data.gross_fallen_due.toFixed(2) : prev.payment_amounts_fallen_due,
          payment_amount: data.payment_amount ? data.payment_amount.toFixed(2) : prev.payment_amount,
          payment_frequency: data.payment_frequency_label || prev.payment_frequency,
          first_missed_payment_date: toISO(data.first_repayment_date) || prev.first_missed_payment_date,
          overall_repayment_amount: (principal + factorAmount + upfrontFees) ? (principal + factorAmount + upfrontFees).toFixed(2) : prev.overall_repayment_amount,
          upfront_fees: upfrontFees ? upfrontFees.toFixed(2) : prev.upfront_fees,
          agreement_date: agreementDate || prev.agreement_date
        }));
        
        setDataLoaded(true);
        toast.success(`Found loan: ${data.borrower_name}`);
        
        // Auto-search ASIC
        if (data.borrower_name) {
          await lookupASICByName(data.borrower_name);
        }
      } else {
        toast.error(response.data.message || "Loan not found");
      }
    } catch (error) {
      console.error("LoanDisk lookup error:", error);
      toast.error(error?.response?.data?.detail || "Failed to lookup loan");
    } finally {
      setLookingUpLoan(false);
    }
  };

  // Auto-lookup ASIC
  const lookupASICByName = async (companyName) => {
    try {
      const response = await api.get(`/aus/asic/search?q=${encodeURIComponent(companyName)}`);
      if (response.data.companies && response.data.companies.length > 0) {
        const company = response.data.companies[0];
        setFormData(prev => ({
          ...prev,
          acn: company.acn || prev.acn,
          abn: company.abn || prev.abn,
          registered_address: company.registered_address || prev.registered_address,
          director_name: company.directors?.[0]?.name || prev.director_name
        }));
        toast.success(`ASIC data loaded`);
      }
    } catch (error) {
      console.log("ASIC auto-lookup didn't find additional data");
    }
  };

  // Manual ASIC Lookup
  const lookupASIC = async () => {
    if (!formData.company_name && !formData.acn) {
      toast.error("Please enter a company name or ACN");
      return;
    }
    
    setLookingUpCompany(true);
    try {
      const searchTerm = formData.acn || formData.company_name;
      const response = await api.get(`/aus/asic/search?q=${encodeURIComponent(searchTerm)}`);
      
      if (response.data.companies && response.data.companies.length > 0) {
        const company = response.data.companies[0];
        setFormData(prev => ({
          ...prev,
          company_name: company.company_name || prev.company_name,
          acn: company.acn || prev.acn,
          abn: company.abn || prev.abn,
          registered_address: company.registered_address || prev.registered_address
        }));
        toast.success(`Found: ${company.company_name}`);
      } else {
        toast.error("Company not found in ASIC");
      }
    } catch (error) {
      toast.error("Failed to lookup company");
    } finally {
      setLookingUpCompany(false);
    }
  };

  // Generate selected documents
  const generateDocuments = async () => {
    if (!formData.company_name) {
      toast.error("Company name is required");
      return;
    }
    
    const totalDue = calculateTotalDue();
    if (totalDue <= 0) {
      toast.error("Total balance due must be greater than zero");
      return;
    }

    if (!selectedDocs.default_notice && !selectedDocs.letter_before_action) {
      toast.error("Please select at least one document to generate");
      return;
    }
    
    setGenerating(true);
    const results = [];
    
    const basePayload = {
      company_name: formData.company_name,
      acn: formData.acn,
      abn: formData.abn,
      registered_address: formData.registered_address,
      sfa_number: formData.sfa_number,
      loan_amount: formData.loan_amount ? parseFloat(formData.loan_amount) : null,
      outstanding_amount: formData.outstanding_amount ? parseFloat(formData.outstanding_amount) : totalDue,
      director_name: formData.director_name,
      director_address: formData.director_address,
      agreement_date: formData.agreement_date,
      default_date: formData.default_date,
      notice_date: formData.notice_date,
      remedy_days: parseInt(formData.remedy_days) || 7,
      breach_details: formData.breach_details,
      factor_amount: formData.factor_amount ? parseFloat(formData.factor_amount) : null,
      default_fee: formData.default_fee ? parseFloat(formData.default_fee) : null,
      late_payment_fees: formData.late_payment_fees ? parseFloat(formData.late_payment_fees) : null,
      payments_received: formData.payments_received ? parseFloat(formData.payments_received) : null,
      payment_amount: formData.payment_amount ? parseFloat(formData.payment_amount) : null,
      payment_frequency: formData.payment_frequency || "week",
      last_payment_date: formData.last_payment_date,
      first_missed_payment_date: formData.first_missed_payment_date,
      payment_amounts_fallen_due: formData.payment_amounts_fallen_due ? parseFloat(formData.payment_amounts_fallen_due) : null,
      overall_repayment_amount: formData.overall_repayment_amount ? parseFloat(formData.overall_repayment_amount) : null,
      upfront_fees: formData.upfront_fees ? parseFloat(formData.upfront_fees) : null
    };

    try {
      // Generate Default Notice if selected
      if (selectedDocs.default_notice) {
        const dnResponse = await api.post("/aus/legal-notices/generate", {
          ...basePayload,
          template_type: "default_notice"
        });
        if (dnResponse.data.success) {
          results.push({
            ...dnResponse.data.document,
            generated_at: new Date().toISOString()
          });
        }
      }

      // Generate Letter Before Action if selected
      if (selectedDocs.letter_before_action) {
        const lbaResponse = await api.post("/aus/legal-notices/generate", {
          ...basePayload,
          template_type: "letter_before_action"
        });
        if (lbaResponse.data.success) {
          results.push({
            ...lbaResponse.data.document,
            generated_at: new Date().toISOString()
          });
        }
      }

      if (results.length > 0) {
        setGeneratedDocs(prev => [...results, ...prev]);
        toast.success(`${results.length} document(s) generated successfully!`);
        setView("list");
        resetForm();
      } else {
        toast.error("Failed to generate documents");
      }
    } catch (error) {
      console.error("Document generation error:", error);
      toast.error(error.response?.data?.detail || "Failed to generate documents");
    } finally {
      setGenerating(false);
    }
  };

  // Download document
  const downloadDocument = (doc) => {
    if (doc.file_base64) {
      const link = document.createElement('a');
      link.href = `data:application/pdf;base64,${doc.file_base64}`;
      link.download = doc.file_name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else if (doc.file_url) {
      window.open(doc.file_url, '_blank');
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      sfa_number: "SFA-",
      company_name: "",
      acn: "",
      abn: "",
      registered_address: "",
      loan_amount: "",
      factor_amount: "",
      outstanding_amount: "",
      payments_received: "",
      default_fee: "",
      late_payment_fees: "",
      director_name: "",
      director_address: "",
      agreement_date: "",
      default_date: "",
      notice_date: new Date().toISOString().split('T')[0],
      remedy_days: "7",
      breach_details: "",
      payment_amount: "",
      payment_frequency: "week",
      last_payment_date: "",
      first_missed_payment_date: "",
      payment_amounts_fallen_due: "",
      overall_repayment_amount: "",
      upfront_fees: ""
    });
    setSelectedDocs({ default_notice: true, letter_before_action: false });
    setDataLoaded(false);
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const formatCurrency = (value) => {
    if (!value) return "$0.00";
    return `$${parseFloat(value).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const totalDue = calculateTotalDue();

  // ============ LIST VIEW ============
  if (view === "list") {
    return (
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Scale className="w-6 h-6" />
              Legal Notices
            </h1>
            <p className="text-muted-foreground">
              Generate Default Notices and Letters Before Action for Australian borrowers
            </p>
          </div>
          <Button onClick={() => setView("create")} data-testid="create-notice-btn">
            <Plus className="w-4 h-4 mr-2" />
            Create Legal Notice
          </Button>
        </div>

        {/* Generated Documents List */}
        {generatedDocs.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <FileText className="w-16 h-16 text-muted-foreground/30 mb-4" />
              <h3 className="text-lg font-medium text-muted-foreground mb-2">No Legal Notices Generated Yet</h3>
              <p className="text-sm text-muted-foreground mb-6">
                Click "Create Legal Notice" to generate Default Notices or Letters Before Action
              </p>
              <Button onClick={() => setView("create")}>
                <Plus className="w-4 h-4 mr-2" />
                Create Your First Notice
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">Generated Documents</h2>
              <Badge variant="outline">{generatedDocs.length} document(s)</Badge>
            </div>
            
            <div className="grid gap-4">
              {generatedDocs.map((doc, index) => (
                <Card key={index} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`p-3 rounded-lg ${doc.type === "Default Notice" ? "bg-amber-100" : "bg-red-100"}`}>
                          {doc.type === "Default Notice" ? (
                            <AlertTriangle className="w-6 h-6 text-amber-600" />
                          ) : (
                            <FileWarning className="w-6 h-6 text-red-600" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium">{doc.file_name}</p>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Badge variant={doc.type === "Default Notice" ? "warning" : "destructive"} className="text-xs">
                              {doc.type}
                            </Badge>
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {new Date(doc.generated_at).toLocaleDateString("en-GB")}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {doc.file_url && (
                          <Button variant="ghost" size="sm" onClick={() => window.open(doc.file_url, '_blank')}>
                            <Eye className="w-4 h-4" />
                          </Button>
                        )}
                        <Button variant="outline" size="sm" onClick={() => downloadDocument(doc)}>
                          <Download className="w-4 h-4 mr-1" />
                          Download
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // ============ CREATE VIEW ============
  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => setView("list")} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Create Legal Notice</h1>
            <p className="text-muted-foreground">
              Step 1: Load borrower data → Step 2: Review details → Step 3: Select documents to generate
            </p>
          </div>
        </div>
        <Button variant="outline" onClick={resetForm}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Reset
        </Button>
      </div>

      {/* Step 1: Data Lookup */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Search className="w-5 h-5" />
            Step 1: Load Borrower & Loan Data
          </CardTitle>
          <CardDescription>
            Enter SFA number to auto-fill from LoanDisk, or search ASIC manually
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* LoanDisk Lookup */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">SFA Number (LoanDisk)</Label>
              <div className="flex gap-2">
                <Input
                  placeholder="SFA-12345"
                  value={formData.sfa_number}
                  onChange={(e) => {
                    let val = e.target.value;
                    if (!val.startsWith("SFA-")) {
                      val = "SFA-" + val.replace(/^SFA-?/i, "");
                    }
                    updateField("sfa_number", val.toUpperCase());
                  }}
                  className="font-mono"
                  data-testid="sfa-input"
                />
                <Button 
                  onClick={lookupLoanDisk} 
                  disabled={lookingUpLoan}
                  data-testid="lookup-loandisk-btn"
                >
                  {lookingUpLoan ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Search className="w-4 h-4 mr-2" />
                      Lookup
                    </>
                  )}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Pulls: company, address, loan amounts, dates from SimplyFundedAU branch
              </p>
            </div>

            {/* ASIC Lookup */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">Company Name / ACN (ASIC)</Label>
              <div className="flex gap-2">
                <Input
                  placeholder="Search company or enter ACN..."
                  value={formData.acn || ""}
                  onChange={(e) => updateField("acn", e.target.value)}
                  data-testid="asic-search-input"
                />
                <Button 
                  onClick={lookupASIC} 
                  disabled={lookingUpCompany}
                  variant="secondary"
                  data-testid="lookup-asic-btn"
                >
                  {lookingUpCompany ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Building2 className="w-4 h-4" />
                  )}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Pulls: ACN, ABN, registered address, directors
              </p>
            </div>
          </div>

          {/* Data Status */}
          {dataLoaded && (
            <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center gap-2 text-green-700">
                <CheckCircle className="w-5 h-5" />
                <span className="font-medium">Data loaded for {formData.company_name}</span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Step 2: Review & Edit Details */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Company & Director */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              Company & Director
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Company Name *</Label>
              <Input
                value={formData.company_name}
                onChange={(e) => updateField("company_name", e.target.value)}
                placeholder="Full company name"
                data-testid="company-name-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>ACN</Label>
                <Input
                  value={formData.acn}
                  onChange={(e) => updateField("acn", e.target.value)}
                  placeholder="9 digits"
                />
              </div>
              <div>
                <Label>ABN</Label>
                <Input
                  value={formData.abn}
                  onChange={(e) => updateField("abn", e.target.value)}
                  placeholder="11 digits"
                />
              </div>
            </div>
            
            <div>
              <Label>Registered Address</Label>
              <Input
                value={formData.registered_address}
                onChange={(e) => updateField("registered_address", e.target.value)}
                placeholder="Full address"
              />
            </div>
            
            <div className="pt-3 border-t">
              <div>
                <Label>Director/Guarantor Name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    value={formData.director_name}
                    onChange={(e) => updateField("director_name", e.target.value)}
                    placeholder="Full name"
                    className="pl-9"
                  />
                </div>
              </div>
              <div className="mt-3">
                <Label>Director Address</Label>
                <Input
                  value={formData.director_address}
                  onChange={(e) => updateField("director_address", e.target.value)}
                  placeholder="Director's address"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Balance Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Calculator className="w-5 h-5" />
              Balance Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <Label className="text-sm">1. Principal Amount</Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  type="number"
                  value={formData.loan_amount}
                  onChange={(e) => updateField("loan_amount", e.target.value)}
                  placeholder="0.00"
                  className="pl-9"
                />
              </div>
            </div>
            
            <div>
              <Label className="text-sm">2. Factor Amount / Interest</Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  type="number"
                  value={formData.factor_amount}
                  onChange={(e) => updateField("factor_amount", e.target.value)}
                  placeholder="0.00"
                  className="pl-9"
                />
              </div>
            </div>
            
            <div>
              <Label className="text-sm flex items-center gap-2">
                3. Default Fee
                <Badge variant="outline" className="text-xs">10% of principal</Badge>
              </Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  type="number"
                  value={formData.default_fee}
                  onChange={(e) => updateField("default_fee", e.target.value)}
                  placeholder="0.00"
                  className="pl-9"
                />
              </div>
            </div>
            
            <div>
              <Label className="text-sm">4. Late Payment Fees</Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  type="number"
                  value={formData.late_payment_fees}
                  onChange={(e) => updateField("late_payment_fees", e.target.value)}
                  placeholder="0.00"
                  className="pl-9"
                />
              </div>
              <p className="text-xs text-muted-foreground mt-1">Penalties charged to date (per LoanDisk)</p>
            </div>
            
            <div>
              <Label className="text-sm text-red-600">5. Less Payments Received</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-red-600 text-sm font-medium">-$</span>
                <Input
                  type="number"
                  value={formData.payments_received}
                  onChange={(e) => updateField("payments_received", e.target.value)}
                  placeholder="0.00"
                  className="pl-9 text-red-600"
                />
              </div>
            </div>
            
            <div>
              <Label className="text-sm">6. Outstanding Balance (per LoanDisk)</Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  type="number"
                  value={formData.outstanding_amount}
                  onChange={(e) => updateField("outstanding_amount", e.target.value)}
                  placeholder="0.00"
                  className="pl-9"
                  data-testid="outstanding-balance-input"
                />
              </div>
              <p className="text-xs text-muted-foreground mt-1">Drives "Balance of Overall Repayment Amount" on the acceleration table</p>
            </div>

            <div className="pt-3 border-t-2 border-primary">
              <div className="flex justify-between items-center">
                <Label className="text-base font-bold">Balance Due</Label>
                <span className="text-xl font-bold text-primary">
                  {formatCurrency(totalDue)}
                </span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Outstanding balance + default fee — total payable on acceleration
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Payment Schedule & Arrears (Default Notice) */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <DollarSign className="w-5 h-5" />
              Payment Schedule & Arrears
            </CardTitle>
            <p className="text-xs text-muted-foreground">Used by the Default Notice arrears tables</p>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm">Payment Amount</Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type="number"
                    value={formData.payment_amount}
                    onChange={(e) => updateField("payment_amount", e.target.value)}
                    placeholder="0.00"
                    className="pl-9"
                    data-testid="payment-amount-input"
                  />
                </div>
              </div>
              <div>
                <Label className="text-sm">Payment Frequency</Label>
                <Input
                  value={formData.payment_frequency}
                  onChange={(e) => updateField("payment_frequency", e.target.value)}
                  placeholder="e.g. week / Friday"
                  data-testid="payment-frequency-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm">Last Payment Received</Label>
                <Input
                  type="date"
                  value={formData.last_payment_date}
                  onChange={(e) => updateField("last_payment_date", e.target.value)}
                  data-testid="last-payment-date-input"
                />
              </div>
              <div>
                <Label className="text-sm">Fallen-Due Period Start</Label>
                <Input
                  type="date"
                  value={formData.first_missed_payment_date}
                  onChange={(e) => updateField("first_missed_payment_date", e.target.value)}
                  data-testid="first-missed-payment-date-input"
                />
                <p className="text-xs text-muted-foreground mt-1">Prefilled with the first repayment date — the fallen-due figure covers all payments from this date</p>
              </div>
            </div>
            <div>
              <Label className="text-sm">Payment Amounts Fallen Due</Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  type="number"
                  value={formData.payment_amounts_fallen_due}
                  onChange={(e) => updateField("payment_amounts_fallen_due", e.target.value)}
                  placeholder="0.00"
                  className="pl-9"
                  data-testid="fallen-due-input"
                />
              </div>
              <p className="text-xs text-muted-foreground mt-1">All scheduled repayments billed from schedule start to today (per LoanDisk). Arrears = this + fees − payments received</p>
            </div>
            <div>
              <Label className="text-sm">Overall Repayment Amount</Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  type="number"
                  value={formData.overall_repayment_amount}
                  onChange={(e) => updateField("overall_repayment_amount", e.target.value)}
                  placeholder="Defaults to Principal + Factor + Upfront Fees"
                  className="pl-9"
                  data-testid="overall-repayment-input"
                />
              </div>
              <p className="text-xs text-muted-foreground mt-1">Total Loan Amount on the notice — includes clause 5.2 upfront fees (loan + factor + fees)</p>
            </div>
          </CardContent>
        </Card>

        {/* Dates & Document Selection */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Dates & Documents
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Agreement/Commencement Date</Label>
              <Input
                type="date"
                value={formData.agreement_date}
                onChange={(e) => updateField("agreement_date", e.target.value)}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Default Date</Label>
                <Input
                  type="date"
                  value={formData.default_date}
                  onChange={(e) => updateField("default_date", e.target.value)}
                />
              </div>
              <div>
                <Label>Notice Date</Label>
                <Input
                  type="date"
                  value={formData.notice_date}
                  onChange={(e) => updateField("notice_date", e.target.value)}
                />
              </div>
            </div>
            
            <div>
              <Label>Days to Pay</Label>
              <Input
                type="number"
                value={formData.remedy_days}
                onChange={(e) => updateField("remedy_days", e.target.value)}
                placeholder="7"
              />
            </div>

            {/* Document Selection - Step 3 */}
            <div className="pt-4 border-t">
              <Label className="text-base font-semibold mb-3 block">Step 3: Select Documents to Generate</Label>
              
              <div className="space-y-3">
                <div 
                  className={`flex items-center space-x-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${selectedDocs.default_notice ? 'border-amber-500 bg-amber-50' : 'border-muted hover:border-amber-300'}`}
                  onClick={() => setSelectedDocs(prev => ({ ...prev, default_notice: !prev.default_notice }))}
                >
                  <Checkbox 
                    checked={selectedDocs.default_notice}
                    onCheckedChange={(checked) => setSelectedDocs(prev => ({ ...prev, default_notice: checked }))}
                    data-testid="checkbox-default-notice"
                  />
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-600" />
                    <div>
                      <p className="font-medium">Default Notice</p>
                      <p className="text-xs text-muted-foreground">Initial notice of default</p>
                    </div>
                  </div>
                </div>
                
                <div 
                  className={`flex items-center space-x-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${selectedDocs.letter_before_action ? 'border-red-500 bg-red-50' : 'border-muted hover:border-red-300'}`}
                  onClick={() => setSelectedDocs(prev => ({ ...prev, letter_before_action: !prev.letter_before_action }))}
                >
                  <Checkbox 
                    checked={selectedDocs.letter_before_action}
                    onCheckedChange={(checked) => setSelectedDocs(prev => ({ ...prev, letter_before_action: checked }))}
                    data-testid="checkbox-letter-before-action"
                  />
                  <div className="flex items-center gap-2">
                    <FileWarning className="w-5 h-5 text-red-600" />
                    <div>
                      <p className="font-medium">Letter Before Action</p>
                      <p className="text-xs text-muted-foreground">Final demand before legal action</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Generate Button */}
            <Button 
              onClick={generateDocuments} 
              className="w-full mt-4"
              disabled={generating || !formData.company_name || totalDue <= 0 || (!selectedDocs.default_notice && !selectedDocs.letter_before_action)}
              size="lg"
              data-testid="generate-btn"
            >
              {generating ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <FileText className="w-4 h-4 mr-2" />
              )}
              Generate {[selectedDocs.default_notice && "DN", selectedDocs.letter_before_action && "LBA"].filter(Boolean).join(" & ")}
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Breach Details */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Additional Details (Optional)</CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea
            value={formData.breach_details}
            onChange={(e) => updateField("breach_details", e.target.value)}
            placeholder="Describe the nature of the default/breach..."
            rows={3}
          />
        </CardContent>
      </Card>
    </div>
  );
}
